import React from "react";
import {
    Alert,
    AlertTitle,
    Box,
    IconButton,
    Slide,
    Snackbar,
    Typography,
} from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";

const SlideTransition = (props) => <Slide {...props} direction="down" />;

// type → severity mapping (kept compatible with old type=1/2 calls)
const resolveSeverity = (type) => {
    if (type === 1 || type === "success") return "success";
    if (type === 2 || type === "error") return "error";
    if (type === 3 || type === "warning") return "warning";
    if (type === 4 || type === "info") return "info";
    return "info";
};

const SEVERITY_ICONS = {
    success: <CheckCircleRoundedIcon fontSize="inherit" />,
    error: <ErrorRoundedIcon fontSize="inherit" />,
    warning: <WarningRoundedIcon fontSize="inherit" />,
    info: <InfoRoundedIcon fontSize="inherit" />,
};

const Notification = ({
    type,
    message,
    title,
    errors,
    open,
    setOpen,
    duration = 5000,
    persistOnError = true,
}) => {
    const severity = resolveSeverity(type);
    const handleClose = (_event, reason) => {
        if (reason === "clickaway") return;
        setOpen(false);
    };

    // For error lists (e.g. row-level Excel validation), keep the snackbar
    // open until the user explicitly dismisses it.
    const hasErrorList = Array.isArray(errors) && errors.length > 0;
    const autoHide = persistOnError && (severity === "error" || hasErrorList) ? null : duration;

    return (
        <Snackbar
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            open={open}
            onClose={handleClose}
            autoHideDuration={autoHide}
            TransitionComponent={SlideTransition}
            sx={(theme) => ({
                width: { xs: "calc(100% - 32px)", sm: "auto" },
                maxWidth: 560,
                // Push below the AppBar (64–68px tall) so the snackbar doesn't
                // tuck under the fixed header. Bumping z-index above the AppBar's
                // (drawer + 1 = 1201) keeps it on top even on routes without one.
                top: { xs: "80px !important", sm: "88px !important" },
                zIndex: theme.zIndex.drawer + 2,
            })}
        >
            <Alert
                severity={severity}
                variant="filled"
                iconMapping={SEVERITY_ICONS}
                action={
                    <IconButton
                        size="small"
                        aria-label="close"
                        color="inherit"
                        onClick={() => setOpen(false)}
                    >
                        <CloseRoundedIcon fontSize="small" />
                    </IconButton>
                }
                sx={{
                    width: "100%",
                    alignItems: hasErrorList ? "flex-start" : "center",
                    boxShadow: "0 10px 32px rgba(15,23,42,0.16)",
                    borderRadius: 2,
                    "& .MuiAlert-message": { width: "100%" },
                }}
            >
                {title && <AlertTitle sx={{ fontWeight: 700 }}>{title}</AlertTitle>}
                {message && (
                    <Typography variant="body2" sx={{ fontWeight: hasErrorList ? 600 : 500 }}>
                        {message}
                    </Typography>
                )}
                {hasErrorList && (
                    <Box
                        component="ul"
                        sx={{
                            mt: 1,
                            mb: 0,
                            pl: 2.5,
                            maxHeight: 220,
                            overflowY: "auto",
                            fontSize: "0.85rem",
                            lineHeight: 1.5,
                        }}
                    >
                        {errors.map((err, i) => (
                            <li key={i}>{err}</li>
                        ))}
                    </Box>
                )}
            </Alert>
        </Snackbar>
    );
};

export default Notification;